/**
 * CompatibilityArchetypes - Travel style matching between partners
 */
import { useState, useEffect } from "react";
import { Heart, Loader2, Lock, Sparkles, Target } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import axios from "axios";
import { API, useAuth } from "@/App";
import { useEntitlements } from "@/hooks/useEntitlements";
import { PaywallScreen, PaywallType } from "@/components/PaywallScreen";

export default function CompatibilityArchetypes() {
  const { token, user } = useAuth();
  const { hasDuoAccess } = useEntitlements();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPaywall, setShowPaywall] = useState(false);

  useEffect(() => {
    fetchCompatibility();
  }, []);

  const fetchCompatibility = async () => {
    try {
      const res = await axios.get(`${API}/premium/duo/compatibility`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
    } catch (error) {
      console.error("Failed to fetch compatibility");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  }

  if (!data?.is_duo) {
    return (
      <Card className="bg-gradient-to-br from-violet-500/10 to-purple-500/10 border-violet-500/20">
        <CardContent className="p-6 text-center">
          <Sparkles className="w-10 h-10 text-violet-500 mx-auto mb-3" />
          <h3 className="font-serif text-lg font-semibold mb-2">Compatibility Archetypes</h3>
          <p className="text-sm text-muted-foreground">{data?.message || "Link a partner to discover your compatibility"}</p>
        </CardContent>
      </Card>
    );
  }

  if (!hasDuoAccess) {
    return (
      <>
        <Card className="relative overflow-hidden border-violet-500/20">
          <div className="absolute inset-0 bg-gradient-to-br from-violet-500/5 to-purple-500/5 backdrop-blur-sm z-10 flex flex-col items-center justify-center">
            <Lock className="w-8 h-8 text-violet-500 mb-2" />
            <p className="text-sm font-medium">Duo Plan Required</p>
            <button onClick={() => setShowPaywall(true)} className="mt-2 px-4 py-1.5 bg-violet-500 text-white text-sm rounded-full">Learn more on duolyst.co.uk</button>
          </div>
          <CardContent className="p-6 blur-sm"><div className="h-32" /></CardContent>
        </Card>
        {showPaywall && <PaywallScreen type={PaywallType.DUO_UPSELL} isOpen={showPaywall} onClose={() => setShowPaywall(false)} />}
      </>
    );
  }

  return (
    <div className="space-y-4" data-testid="compatibility-archetypes">
      {/* Compatibility Score */}
      <Card className="bg-gradient-to-br from-violet-500/10 to-purple-500/10 border-violet-500/20">
        <CardContent className="p-5 text-center">
          <div className="relative w-24 h-24 mx-auto mb-3">
            <svg className="w-24 h-24 -rotate-90" viewBox="0 0 96 96">
              <circle cx="48" cy="48" r="42" fill="none" stroke="rgba(139,92,246,0.2)" strokeWidth="6" />
              <circle cx="48" cy="48" r="42" fill="none" stroke="#8B5CF6" strokeWidth="6" strokeDasharray={`${data.compatibility_score * 2.64} 264`} strokeLinecap="round" />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-2xl font-bold text-foreground">{data.compatibility_score}%</span>
            </div>
          </div>
          <p className="text-sm font-medium text-foreground">Travel Compatibility</p>
          <p className="text-xs text-muted-foreground">with {data.partner_name}</p>
        </CardContent>
      </Card>

      {/* Archetypes */}
      <div className="grid grid-cols-2 gap-3">
        <Card>
          <CardContent className="p-3 text-center">
            <p className="text-2xl mb-1">{data.my_archetype.emoji}</p>
            <p className="font-medium text-sm text-foreground">{data.my_archetype.name}</p>
            <p className="text-xs text-muted-foreground">You</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 text-center">
            <p className="text-2xl mb-1">{data.partner_archetype.emoji}</p>
            <p className="font-medium text-sm text-foreground">{data.partner_archetype.name}</p>
            <p className="text-xs text-muted-foreground">{data.partner_name?.split(' ')[0]}</p>
          </CardContent>
        </Card>
      </div>

      {/* Couple Archetype */}
      <Card className="border-violet-500/20">
        <CardContent className="p-4 text-center">
          <p className="text-3xl mb-1">{data.couple_archetype.emoji}</p>
          <p className="font-serif text-lg font-semibold text-foreground">The {data.couple_archetype.name}</p>
          <p className="text-xs text-muted-foreground">Your couple travel style</p>
        </CardContent>
      </Card>

      {/* Shared Interests */}
      {data.shared_interests?.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-1.5">
              <Heart className="w-4 h-4 text-pink-500" />
              Shared Interests
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3 pt-0">
            <div className="flex flex-wrap gap-1.5">
              {data.shared_interests.map(interest => (
                <Badge key={interest} className="bg-pink-500/10 text-pink-500 border-pink-500/30 capitalize">{interest}</Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
